import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import './CandidateList.css';

const API_BASE_URL =
  process.env.REACT_APP_API_URL || 'http://localhost:4000';

const CANDIDATES_API = `${API_BASE_URL}/api/candidates`;
const APPLICATIONS_API = `${API_BASE_URL}/api/applications`;
const REQUIREMENTS_API = `${API_BASE_URL}/api/requirements`;

const CandidateList = () => {
  const [candidates, setCandidates] = useState([]);

  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');

  const [page, setPage] = useState(1);
  const [limit] = useState(10);

  const [pagination, setPagination] = useState({
    page: 1,
    limit: 10,
    total: 0,
    totalPages: 1,
  });

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const [showApplyModal, setShowApplyModal] = useState(false);

  const [selectedCandidate, setSelectedCandidate] = useState(null);

  const [requirements, setRequirements] = useState([]);
  const [requirementsLoading, setRequirementsLoading] = useState(false);

  const [selectedRequirement, setSelectedRequirement] = useState(null);

  const [preferredLocation, setPreferredLocation] = useState('');

  const [combinedDocument, setCombinedDocument] = useState(null);

  const [savingApplication, setSavingApplication] = useState(false);

  const token = localStorage.getItem('token');

  const authHeaders = token
    ? {
        Authorization: `Bearer ${token}`,
      }
    : {};

  const getCandidates = useCallback(async () => {
    try {
      setLoading(true);
      setError('');

      const response = await axios.get(CANDIDATES_API, {
        params: {
          page,
          limit,
          q: search,
        },
        headers: authHeaders,
      });

      const result = response.data?.data || response.data;

      setCandidates(result?.items || []);

      setPagination(
        result?.pagination || {
          page,
          limit,
          total: 0,
          totalPages: 1,
        }
      );
    } catch (err) {
      console.error('Candidate fetch error:', err);

      setCandidates([]);

      setError(
        err?.response?.data?.message ||
          'Unable to fetch candidate list.'
      );
    } finally {
      setLoading(false);
    }
  }, [page, limit, search]);

  useEffect(() => {
    getCandidates();
  }, [getCandidates]);

  const handleSearch = (e) => {
    e.preventDefault();

    setPage(1);
    setSearch(searchInput.trim());
  };

  const handleClear = () => {
    setSearchInput('');
    setSearch('');
    setPage(1);
  };

  const changePage = (newPage) => {
    if (loading) return;
    if (newPage < 1) return;
    if (newPage > pagination.totalPages) return;
    if (newPage === page) return;

    setPage(newPage);
  };

  const getPageNumbers = () => {
    const totalPages = pagination.totalPages;

    if (totalPages <= 7) {
      return Array.from(
        { length: totalPages },
        (_, i) => i + 1
      );
    }

    if (page <= 4) {
      return [1, 2, 3, 4, 5, '...', totalPages];
    }

    if (page >= totalPages - 3) {
      return [
        1,
        '...',
        totalPages - 4,
        totalPages - 3,
        totalPages - 2,
        totalPages - 1,
        totalPages,
      ];
    }

    return [
      1,
      '...',
      page - 1,
      page,
      page + 1,
      '...',
      totalPages,
    ];
  };

  const startRecord =
    pagination.total === 0
      ? 0
      : (page - 1) * limit + 1;

  const endRecord = Math.min(
    page * limit,
    pagination.total
  );

  const loadRequirements = async () => {
    try {
      setRequirementsLoading(true);

      const response = await axios.get(REQUIREMENTS_API, {
        params: {
          status: 'open',
        },
        headers: authHeaders,
      });

      const result = response.data?.data || response.data;

      let items = [];

      if (Array.isArray(result)) {
        items = result;
      } else if (Array.isArray(result?.items)) {
        items = result.items;
      } else if (Array.isArray(result?.requirements)) {
        items = result.requirements;
      }

      setRequirements(items);
    } catch (err) {
      console.error('Requirement fetch error:', err);

      setRequirements([]);

      alert(
        err?.response?.data?.message ||
          'Unable to load available jobs.'
      );
    } finally {
      setRequirementsLoading(false);
    }
  };

  const openAddForJob = async (candidate) => {
    setSelectedCandidate(candidate);

    setSelectedRequirement(null);
    setPreferredLocation('');
    setCombinedDocument(null);

    setShowApplyModal(true);

    await loadRequirements();
  };

  const closeApplyModal = () => {
    if (savingApplication) return;

    setShowApplyModal(false);

    setSelectedCandidate(null);
    setSelectedRequirement(null);

    setPreferredLocation('');
    setCombinedDocument(null);
  };

  const getRequirementLocations = (requirement) => {
    if (!requirement) {
      return [];
    }

    let locations = requirement.locations;

    if (!locations) {
      return [];
    }

    if (typeof locations === 'string') {
      try {
        locations = JSON.parse(locations);
      } catch (error) {
        return [];
      }
    }

    if (!Array.isArray(locations)) {
      return [];
    }

    return locations;
  };

  const getLocationName = (location) => {
    if (!location) {
      return '';
    }

    if (typeof location === 'string') {
      return location;
    }

    return (
      location.name ||
      location.location ||
      location.title ||
      ''
    );
  };

  const isLocationOpen = (location) => {
    if (!location || typeof location !== 'object') {
      return true;
    }

    if (
      location.is_open === false ||
      location.is_open === 0 ||
      location.is_open === 'false'
    ) {
      return false;
    }

    if (location.is_selectable === false) {
      return false;
    }

    if (
      location.remaining_vacancy !== undefined &&
      location.remaining_vacancy !== null
    ) {
      return Number(location.remaining_vacancy) > 0;
    }

    return true;
  };

  const getAvailableLocations = (requirement) => {
    const locations = getRequirementLocations(requirement);

    return locations.filter((location) => {
      const name = getLocationName(location);

      if (!name) {
        return false;
      }

      return isLocationOpen(location);
    });
  };

  const handleRequirementChange = (e) => {
    const requirementId = e.target.value;

    if (!requirementId) {
      setSelectedRequirement(null);
      setPreferredLocation('');
      return;
    }

    const selected =
      requirements.find(
        (item) =>
          String(item.id) === String(requirementId)
      ) || null;

    setSelectedRequirement(selected);
    setPreferredLocation('');

    if (selected) {
      const locations = getAvailableLocations(selected);

      if (locations.length === 1) {
        setPreferredLocation(
          getLocationName(locations[0])
        );
      } else if (
        locations.length === 0 &&
        selected.location
      ) {
        setPreferredLocation(selected.location);
      }
    }
  };

  const handleLocationChange = (e) => {
    setPreferredLocation(e.target.value);
  };

  const handleDocumentChange = (e) => {
    const file = e.target.files?.[0] || null;

    if (!file) {
      setCombinedDocument(null);
      return;
    }

    const isPdf =
      file.type === 'application/pdf' ||
      file.name.toLowerCase().endsWith('.pdf');

    if (!isPdf) {
      alert('Please upload a PDF file only.');

      e.target.value = '';
      setCombinedDocument(null);

      return;
    }

    setCombinedDocument(file);
  };

  const handleSubmitApplication = async (e) => {
    e.preventDefault();

    if (!selectedCandidate) {
      alert('Candidate not selected.');
      return;
    }

    if (!selectedRequirement?.id) {
      alert('Please select a job.');
      return;
    }

    const availableLocations =
      getAvailableLocations(selectedRequirement);

    if (
      availableLocations.length > 0 &&
      !preferredLocation
    ) {
      alert('Please select a location.');
      return;
    }

    if (!combinedDocument) {
      alert(
        'Please upload the candidate document PDF.'
      );
      return;
    }

    try {
      setSavingApplication(true);

      const formData = new FormData();

      formData.append(
        'candidate_id',
        selectedCandidate.id
      );

      formData.append(
        'requirement_id',
        selectedRequirement.id
      );

      formData.append(
        'preferred_location',
        preferredLocation || ''
      );

      formData.append(
        'file',
        combinedDocument
      );

      const response = await axios.post(
        `${APPLICATIONS_API}/assign`,
        formData,
        {
          headers: {
            ...authHeaders,
          },
        }
      );

      const result =
        response.data?.data ||
        response.data;

      if (!result) {
        throw new Error(
          'Application assignment failed.'
        );
      }

      alert(
        'Candidate successfully added for the selected job.'
      );

      closeApplyModal();

      await getCandidates();
    } catch (err) {
      console.error(
        'Admin application error:',
        err
      );

      alert(
        err?.response?.data?.message ||
          err?.message ||
          'Unable to submit application.'
      );
    } finally {
      setSavingApplication(false);
    }
  };

  return (
    <div className="candidate-page">
      <div className="candidate-container">

        <div className="candidate-header">
          <div>
            <h2>Candidate List</h2>

            <p>
              Total Candidates:{' '}
              <strong>
                {pagination.total}
              </strong>
            </p>
          </div>

          <button
            type="button"
            className="refresh-btn"
            onClick={getCandidates}
            disabled={loading}
          >
            {loading
              ? 'Loading...'
              : 'Refresh'}
          </button>
        </div>

        <form
          className="candidate-search"
          onSubmit={handleSearch}
        >
          <input
            type="text"
            value={searchInput}
            onChange={(e) =>
              setSearchInput(e.target.value)
            }
            placeholder="Search by candidate name or mobile number"
          />

          <button type="submit">
            Search
          </button>

          {search && (
            <button
              type="button"
              className="clear-btn"
              onClick={handleClear}
            >
              Clear
            </button>
          )}
        </form>

        {search && (
          <div className="search-info">
            Search results for:{' '}
            <strong>{search}</strong>
          </div>
        )}

        {error && (
          <div className="candidate-error">
            {error}
          </div>
        )}

        <div className="candidate-table-wrapper">
          <table className="candidate-table">
            <thead>
              <tr>
                <th>#</th>
                <th>Name</th>
                <th>Mobile</th>
                <th>Email</th>
                <th>Location</th>
                <th>Department</th>
                <th>Experience</th>
                <th>Applications</th>
                <th>Resume</th>
                <th>Status</th>
                <th>Action</th>
              </tr>
            </thead>

            <tbody>
              {loading ? (
                <tr>
                  <td
                    colSpan="11"
                    className="table-message"
                  >
                    Loading candidates...
                  </td>
                </tr>
              ) : candidates.length === 0 ? (
                <tr>
                  <td
                    colSpan="11"
                    className="table-message"
                  >
                    No candidates found.
                  </td>
                </tr>
              ) : (
                candidates.map(
                  (candidate, index) => (
                    <tr
                      key={candidate.id}
                    >
                      <td>
                        {(page - 1) *
                          limit +
                          index +
                          1}
                      </td>

                      <td>
                        <strong>
                          {candidate.name ||
                            '-'}
                        </strong>
                      </td>

                      <td>
                        {candidate.mobile ||
                          '-'}
                      </td>

                      <td>
                        {candidate.email ||
                          '-'}
                      </td>

                      <td>
                        {candidate.location ||
                          '-'}
                      </td>

                      <td>
                        {candidate.department ||
                          '-'}
                      </td>

                      <td>
                        {candidate.experience_years !==
                          null &&
                        candidate.experience_years !==
                          undefined
                          ? `${candidate.experience_years} years`
                          : '-'}
                      </td>

                      <td>
                        {candidate.applications_count ||
                          0}
                      </td>

                      <td>
                        {candidate.has_resume ? (
                          <span className="status-badge resume">
                            Available
                          </span>
                        ) : (
                          <span className="status-badge no-resume">
                            Not Available
                          </span>
                        )}
                      </td>

                      <td>
                        {candidate.is_active ? (
                          <span className="status-badge active">
                            Active
                          </span>
                        ) : (
                          <span className="status-badge inactive">
                            Inactive
                          </span>
                        )}
                      </td>

                      <td>
                        <button
                          type="button"
                          className="add-job-btn"
                          onClick={() =>
                            openAddForJob(
                              candidate
                            )
                          }
                        >
                          Add for Job
                        </button>
                      </td>
                    </tr>
                  )
                )
              )}
            </tbody>
          </table>
        </div>

        {!loading &&
          pagination.total > 0 && (
            <div className="pagination-wrapper">

              <div className="pagination-info">
                Showing{' '}
                <strong>
                  {startRecord}
                </strong>{' '}
                to{' '}
                <strong>
                  {endRecord}
                </strong>{' '}
                of{' '}
                <strong>
                  {pagination.total}
                </strong>
              </div>

              <div className="pagination">

                <button
                  type="button"
                  disabled={
                    page === 1 ||
                    loading
                  }
                  onClick={() =>
                    changePage(
                      page - 1
                    )
                  }
                >
                  Previous
                </button>

                {getPageNumbers().map(
                  (item, index) => {
                    if (
                      item === '...'
                    ) {
                      return (
                        <span
                          key={`dots-${index}`}
                          className="pagination-dots"
                        >
                          ...
                        </span>
                      );
                    }

                    return (
                      <button
                        type="button"
                        key={item}
                        className={
                          page === item
                            ? 'active-page'
                            : ''
                        }
                        disabled={
                          loading
                        }
                        onClick={() =>
                          changePage(
                            item
                          )
                        }
                      >
                        {item}
                      </button>
                    );
                  }
                )}

                <button
                  type="button"
                  disabled={
                    page ===
                      pagination.totalPages ||
                    loading
                  }
                  onClick={() =>
                    changePage(
                      page + 1
                    )
                  }
                >
                  Next
                </button>

              </div>
            </div>
          )}

        {showApplyModal && (
          <div
            className="apply-modal-overlay"
            onMouseDown={(e) => {
              if (
                e.target ===
                e.currentTarget
              ) {
                closeApplyModal();
              }
            }}
          >
            <div className="apply-modal">

              <div className="apply-modal-header">
                <div>
                  <h2>
                    Add Candidate for Job
                  </h2>

                  {selectedCandidate && (
                    <p>
                      Candidate:{' '}
                      <strong>
                        {
                          selectedCandidate.name
                        }
                      </strong>

                      {' | '}

                      Mobile:{' '}
                      <strong>
                        {
                          selectedCandidate.mobile ||
                          '-'
                        }
                      </strong>
                    </p>
                  )}
                </div>

                <button
                  type="button"
                  className="modal-close-btn"
                  onClick={
                    closeApplyModal
                  }
                  disabled={
                    savingApplication
                  }
                >
                  ×
                </button>
              </div>

              <form
                className="apply-modal-body"
                onSubmit={
                  handleSubmitApplication
                }
              >

                <div className="job-select-section">
                  <label>
                    Select Job
                    <span className="required-star">
                      *
                    </span>
                  </label>

                  {requirementsLoading ? (
                    <div className="loading-box">
                      Loading available
                      jobs...
                    </div>
                  ) : (
                    <select
                      value={
                        selectedRequirement?.id ||
                        ''
                      }
                      onChange={
                        handleRequirementChange
                      }
                      required
                    >
                      <option value="">
                        Select a job
                      </option>

                      {requirements.map(
                        (requirement) => (
                          <option
                            key={
                              requirement.id
                            }
                            value={
                              requirement.id
                            }
                          >
                            {requirement.title ||
                              requirement.name ||
                              requirement.job_title ||
                              requirement.position ||
                              'Untitled Job'}
                          </option>
                        )
                      )}
                    </select>
                  )}
                </div>

                {selectedRequirement && (
                  <div className="selected-job-card">

                    <h3>
                      {selectedRequirement.title ||
                        selectedRequirement.name ||
                        selectedRequirement.job_title ||
                        selectedRequirement.position ||
                        'Selected Job'}
                    </h3>

                    <div className="job-info-grid">

                      {selectedRequirement.department && (
                        <div>
                          <span>
                            Department
                          </span>

                          <strong>
                            {
                              selectedRequirement.department
                            }
                          </strong>
                        </div>
                      )}

                      {selectedRequirement.location && (
                        <div>
                          <span>
                            Location
                          </span>

                          <strong>
                            {
                              selectedRequirement.location
                            }
                          </strong>
                        </div>
                      )}

                      {selectedRequirement.positions && (
                        <div>
                          <span>
                            Positions
                          </span>

                          <strong>
                            {
                              selectedRequirement.positions
                            }
                          </strong>
                        </div>
                      )}

                    </div>
                  </div>
                )}

                {selectedRequirement && (
                  <div className="apply-field">
                    <label>
                      Preferred Location
                      {getAvailableLocations(
                        selectedRequirement
                      ).length > 0 && (
                        <span className="required-star">
                          *
                        </span>
                      )}
                    </label>

                    {getAvailableLocations(
                      selectedRequirement
                    ).length > 0 ? (
                      <select
                        value={
                          preferredLocation
                        }
                        onChange={
                          handleLocationChange
                        }
                        required
                      >
                        <option value="">
                          Select location
                        </option>

                        {getAvailableLocations(
                          selectedRequirement
                        ).map(
                          (location, index) => {
                            const locationName =
                              getLocationName(
                                location
                              );

                            return (
                              <option
                                key={`${locationName}-${index}`}
                                value={
                                  locationName
                                }
                              >
                                {locationName}
                                {location &&
                                  typeof location ===
                                    'object' &&
                                  location.remaining_vacancy !==
                                    undefined &&
                                  location.remaining_vacancy !==
                                    null
                                  ? ` (${location.remaining_vacancy} vacancy)`
                                  : ''}
                              </option>
                            );
                          }
                        )}
                      </select>
                    ) : (
                      <input
                        type="text"
                        value={
                          preferredLocation
                        }
                        onChange={(e) =>
                          setPreferredLocation(
                            e.target.value
                          )
                        }
                        placeholder="Enter preferred location"
                      />
                    )}
                  </div>
                )}

                {selectedRequirement && (
                  <div className="combined-document-section">

                    <div className="section-title">
                      Candidate Documents
                    </div>

                    <div className="document-upload-box">

                      <div className="document-upload-content">

                        <h3>
                          Upload Combined Document
                        </h3>

                        <p>
                          Upload one PDF containing
                          all candidate documents.
                        </p>

                        <label className="document-upload-label">
                          <input
                            type="file"
                            accept="application/pdf,.pdf"
                            onChange={
                              handleDocumentChange
                            }
                            disabled={
                              savingApplication
                            }
                          />

                          <span>
                            Choose PDF
                          </span>
                        </label>

                        {combinedDocument && (
                          <div className="selected-document">

                            <div>
                              <strong>
                                Selected Document:
                              </strong>{' '}
                              {
                                combinedDocument.name
                              }
                            </div>

                            <div>
                              Size:{' '}
                              {(
                                combinedDocument.size /
                                (1024 * 1024)
                              ).toFixed(2)}{' '}
                              MB
                            </div>

                          </div>
                        )}

                      </div>
                    </div>

                    <div className="document-note">
                      <strong>
                        Note:
                      </strong>{' '}
                      Please upload all candidate
                      documents together in one PDF.
                    </div>

                  </div>
                )}

                <div className="apply-modal-footer">

                  <button
                    type="button"
                    className="cancel-apply-btn"
                    onClick={
                      closeApplyModal
                    }
                    disabled={
                      savingApplication
                    }
                  >
                    Cancel
                  </button>

                  <button
                    type="submit"
                    className="submit-apply-btn"
                    disabled={
                      savingApplication ||
                      !selectedRequirement ||
                      !combinedDocument ||
                      requirementsLoading
                    }
                  >
                    {savingApplication
                      ? 'Submitting...'
                      : 'Save & Apply'}
                  </button>

                </div>

              </form>
            </div>
          </div>
        )}

      </div>
    </div>
  );
};

export default CandidateList;